import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import api from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const [invitePreview, setInvitePreview] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    gender: "male",
    profile_for: "self",
    phone: "",
  });
  const [loading, setLoading] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!ref) return;
    (async () => {
      try {
        const { data } = await api.get(`/referrals/preview/${ref}`);
        setInvitePreview(data);
        if (data.invitee_name && !form.name) upd("name", data.invitee_name);
      } catch (e) {
        setInvitePreview(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register({ ...form, ref: ref || undefined });
      toast.success(`Ahlan wa sahlan, ${u.name?.split(" ")[0]}! Just five quick questions.`);
      nav("/welcome");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-16" data-testid="register-page">
      {invitePreview && (
        <div className="max-w-2xl mx-auto mb-10 bg-white border border-brand-line p-6 md:p-8" data-testid="invite-banner">
          <div className="label-eyebrow text-brand-terracotta">You've been invited</div>
          <div className="mt-2 font-serif text-2xl md:text-3xl text-brand-ink leading-snug">
            <em className="text-brand-terracotta">{invitePreview.inviter_name}</em> invited you{invitePreview.relationship ? ` as their ${invitePreview.relationship}` : ""} to join WR Rishtey.
          </div>
          {invitePreview.note && (
            <blockquote className="mt-4 pl-4 border-l-2 border-brand-gold text-brand-muted italic">
              "{invitePreview.note}"
            </blockquote>
          )}
        </div>
      )}

      <div className="text-center max-w-2xl mx-auto">
        <div className="label-eyebrow text-brand-terracotta">Begin</div>
        <h1 className="font-serif text-5xl md:text-6xl mt-3 text-brand-ink tracking-tighter leading-none">
          A profile,<br />with <em className="text-brand-terracotta">intention.</em>
        </h1>
        <p className="mt-6 text-brand-muted leading-relaxed">
          Registration is complimentary. You'll complete a detailed dossier once you're inside — take your time.
        </p>
      </div>

      <form onSubmit={onSubmit} className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6 bg-white border border-brand-line p-8 md:p-12 max-w-3xl mx-auto">
        <div className="sm:col-span-2">
          <Label className="label-eyebrow text-brand-muted">This profile is for</Label>
          <Select value={form.profile_for} onValueChange={(v) => upd("profile_for", v)}>
            <SelectTrigger data-testid="reg-profile-for" className="mt-2 h-12 rounded-none border-brand-line">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="self">Myself</SelectItem>
              <SelectItem value="son">My son</SelectItem>
              <SelectItem value="daughter">My daughter</SelectItem>
              <SelectItem value="sibling">My sibling</SelectItem>
              <SelectItem value="friend">A friend</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="label-eyebrow text-brand-muted">Full name</Label>
          <Input
            value={form.name} onChange={(e) => upd("name", e.target.value)}
            required data-testid="reg-name-input"
            className="mt-2 h-12 rounded-none border-brand-line"
            placeholder="Ahmed bin Yusuf"
          />
        </div>
        <div>
          <Label className="label-eyebrow text-brand-muted">Gender</Label>
          <RadioGroup value={form.gender} onValueChange={(v) => upd("gender", v)} className="mt-2 flex gap-6 h-12 items-center" data-testid="reg-gender">
            <label className="flex items-center gap-2 cursor-pointer">
              <RadioGroupItem value="male" data-testid="reg-gender-male" /> Brother
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <RadioGroupItem value="female" data-testid="reg-gender-female" /> Sister
            </label>
          </RadioGroup>
        </div>

        <div>
          <Label className="label-eyebrow text-brand-muted">Email</Label>
          <Input type="email" required value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="reg-email-input" className="mt-2 h-12 rounded-none border-brand-line" placeholder="you@family.com" />
        </div>
        <div>
          <Label className="label-eyebrow text-brand-muted">Phone (optional)</Label>
          <Input value={form.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="reg-phone-input" className="mt-2 h-12 rounded-none border-brand-line" placeholder="+91 98765 43210" />
        </div>

        <div className="sm:col-span-2">
          <Label className="label-eyebrow text-brand-muted">Password</Label>
          <Input type="password" required value={form.password} onChange={(e) => upd("password", e.target.value)} data-testid="reg-password-input" className="mt-2 h-12 rounded-none border-brand-line" placeholder="At least 6 characters" />
        </div>

        <Button type="submit" disabled={loading} data-testid="reg-submit-btn" className="sm:col-span-2 h-14 rounded-full bg-brand-terracotta hover:bg-brand-terracotta/90 text-white text-base">
          {loading ? "Creating…" : "Create My Profile"}
        </Button>

        <p className="sm:col-span-2 text-sm text-brand-muted text-center">
          Already have an account?{" "}
          <Link to="/login" className="text-brand-terracotta underline underline-offset-4">Sign in</Link>
        </p>
      </form>
    </div>
  );
}
